import type { HelpMenuGroup, HelpMenuItem, HelpPage, HelpTitle } from '@/help/helpPages';
import { useAuth } from '@/composables/useAuth';
import { usePermissions } from '@/composables/usePermissions';
import { useSettings } from '@/composables/useSettings';
import { findHelpPage, helpMenu, helpPages } from '@/help/helpPages';

export interface VisibleHelpMenuItem extends HelpMenuItem {
  page: HelpPage;
  to: string;
}

export interface VisibleHelpMenuSection {
  titleKey: string;
  items: VisibleHelpMenuItem[];
}

export interface VisibleHelpMenuGroup extends Omit<HelpMenuGroup, 'sections'> {
  sections: VisibleHelpMenuSection[];
}

/**
 * Decides which User Guide pages the current user may see.
 *
 * This is the one rule behind the help routes, the Help menu and the help search. Like
 * `useSettings`, it reads the user's permissions and the settings when called, so call it where
 * the answer is needed.
 */
export function useHelpPages() {
  const { authUser } = useAuth();
  const { canAny } = usePermissions();
  const { getSetting } = useSettings();

  function isHelpPageVisible(page: HelpPage): boolean {
    if (page.adminOnly && !authUser?.is_admin) {
      return false;
    }
    if (page.permissions && page.permissions.length > 0 && !canAny(page.permissions)) {
      return false;
    }
    return page.isVisible
      ? page.isVisible(getSetting)
      : true;
  }

  /** Whether `path` (a help page, optionally with an `#anchor`) may be opened. Unknown paths are not help pages, so they are not blocked here. */
  function isHelpPathVisible(path: string): boolean {
    const page = findHelpPage(path);
    return page
      ? isHelpPageVisible(page)
      : true;
  }

  function resolveHelpTitle(page: HelpPage): HelpTitle {
    return typeof page.title === 'function'
      ? page.title(getSetting)
      : page.title;
  }

  function visibleHelpPages(): HelpPage[] {
    return helpPages.filter(isHelpPageVisible);
  }

  /** The Help menu groups with only the entries the user may see. Empty sections and groups are left out. */
  function visibleHelpMenu(): VisibleHelpMenuGroup[] {
    return helpMenu
      .map(group => ({
        ...group,
        sections: group.sections
          .map(section => ({
            titleKey: section.titleKey,
            items: section.items.flatMap((item): VisibleHelpMenuItem[] => {
              const page = findHelpPage(item.path);
              if (!page || !isHelpPageVisible(page) || (item.permissions && !canAny(item.permissions))) {
                return [];
              }
              return [
                {
                  ...item,
                  page,
                  to: item.appendHash
                    ? `${item.path}#`
                    : item.path,
                },
              ];
            }),
          }))
          .filter(section => section.items.length > 0),
      }))
      .filter(group => group.sections.length > 0);
  }

  return {
    isHelpPageVisible,
    isHelpPathVisible,
    resolveHelpTitle,
    visibleHelpPages,
    visibleHelpMenu,
  };
}
